import { cpSync, existsSync, mkdirSync } from "node:fs";
import { dirname, join } from "node:path";
import { fileURLToPath } from "node:url";

import {
  WITHDRAW_CIRCUIT_WASM,
  WITHDRAW_CIRCUIT_ZKEY,
} from "../circuits/constants";

const pluginDir = dirname(fileURLToPath(import.meta.url));
const circuitsSrc = join(pluginDir, "..", "circuits");

export type ArcaneCircuitsPluginOptions = {
  /** Directory under `public/` (default: `"circuits"`). */
  dest?: string;
};

type VitePlugin = {
  name: string;
  apply?: "build" | "serve";
  buildStart?: (this: {
    error: (msg: string) => void;
    info: (msg: string) => void;
  }) => void;
};

/**
 * Vite plugin: copies withdraw.wasm and withdraw.zkey into `public/circuits/`
 * before production build so browser proof generation works on static hosts (Vercel, etc.).
 *
 * @example
 * ```ts
 * import { arcaneCircuitsPlugin } from "arcane-privacy-sdk/vite";
 *
 * export default defineConfig({
 *   plugins: [react(), arcaneCircuitsPlugin()],
 * });
 * ```
 */
export function arcaneCircuitsPlugin(
  options: ArcaneCircuitsPluginOptions = {}
): VitePlugin {
  const destDir = options.dest ?? "circuits";

  return {
    name: "arcane-circuits",
    apply: "build",
    buildStart() {
      const target = join(process.cwd(), "public", destDir);
      mkdirSync(target, { recursive: true });

      for (const file of [WITHDRAW_CIRCUIT_WASM, WITHDRAW_CIRCUIT_ZKEY]) {
        const from = join(circuitsSrc, file);
        if (!existsSync(from)) {
          this.error(`Missing circuit artifact: ${from}`);
          return;
        }
        cpSync(from, join(target, file));
      }

      this.info(`Copied withdraw circuits to public/${destDir}/`);
    },
  };
}
